import type { TaxonomyMode } from "./submissions.service";
import * as api from "./client";

export type SurveyStatus = "draft" | "published";

export type SurveyRegionType = "country" | "bar" | "other";

export type SurveyRefereeType = "firm" | "lawyer" | "barrister" | "mediator";

export type SurveyRefereeCategory = "instructing" | "other";

export type SurveyResponseStatus = "draft" | "completed";

export type SurveyResponse = {
  id: string;
  surveyId: string;
  refereeCode: string;
  refereeEmail: string;
  firmRef: string;
  edition: string | null;
  publicationId: number | null;
  editionId: string | null;
  rankingEntityId: string | null;
  bpaId: string | null;
  editionBpaId: string | null;
  rankingTableId: string | null;
  status: SurveyResponseStatus;
  locale: string;
  completedAt: string | null;
  createdAt: string;
  responseData: Record<string, unknown>;
  randomQuestions: unknown;
  surveyDefinition?: unknown;
};

export type Survey = {
  id: string;
  name: string;
  edition: string;
  publication: string;
  publicationId: number | null;
  editionId: string | null;
  defaultLanguage: string;
  status: SurveyStatus;
  version: number;
  lockVersion: number;
  definition: object;
  randomQuestionCount: number;
  /** Empty/omitted arrays = general survey; any non-empty array makes this an exception survey. */
  regionTypes?: SurveyRegionType[];
  refereeTypes?: SurveyRefereeType[];
  refereeCategories?: SurveyRefereeCategory[];
  publishedBy: string | null;
  publishedAt: string | null;
  createdBy: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
};

export type LegacySurveyEditionFields = {
  edition: string;
  publication: string;
  publicationId: number;
  editionId?: never;
};

export type NewAxisSurveyEditionFields = {
  editionId: string;
  publicationId?: number;
  edition?: never;
  publication?: never;
};

export type SurveyEditionFields = LegacySurveyEditionFields | NewAxisSurveyEditionFields;

type SurveyBodyFields = {
  name: string;
  defaultLanguage?: string;
  definition?: object;
  randomQuestionCount?: number;
  regionTypes?: SurveyRegionType[];
  refereeTypes?: SurveyRefereeType[];
  refereeCategories?: SurveyRefereeCategory[];
};

export type CreateSurveyBody = SurveyBodyFields & SurveyEditionFields;

export type UpdateSurveyBody = SurveyBodyFields &
  SurveyEditionFields & {
    status?: SurveyStatus;
    version: number;
    lockVersion: number;
  };

export type SurveySortField = "name" | "publication" | "edition" | "status";

export type ListSurveysParams = {
  edition?: string;
  publication?: string;
  publicationId?: number;
  editionId?: string;
  taxonomy?: TaxonomyMode;
  status?: SurveyStatus;
  sortField?: SurveySortField;
  sortDirection?: "asc" | "desc";
  limit?: number;
  offset?: number;
};

export type ListSurveyResponsesParams = {
  surveyId?: string;
  edition?: string;
  publicationId?: number;
  editionId?: string;
  taxonomy?: TaxonomyMode;
  status?: string;
  firmRef?: string;
  refereeEmail?: string;
  limit?: number;
  offset?: number;
};

type ListSurveysResponse = {
  surveys: Survey[];
  total: number;
  limit: number;
  offset: number;
  requestId: string;
};

type SingleSurveyResponse = {
  survey: Survey;
  requestId: string;
};

type DeleteSurveyResponse = {
  message: string;
  requestId: string;
};

export const surveysService = {
  list: async (params?: ListSurveysParams): Promise<ListSurveysResponse> => {
    const searchParams = new URLSearchParams();
    if (params?.edition) searchParams.append("edition", params.edition);
    if (params?.publication) searchParams.append("publication", params.publication);
    if (params?.publicationId !== undefined)
      searchParams.append("publicationId", params.publicationId.toString());
    if (params?.editionId) searchParams.append("editionId", params.editionId);
    if (params?.taxonomy) searchParams.append("taxonomy", params.taxonomy);
    if (params?.status) searchParams.append("status", params.status);
    if (params?.sortField) searchParams.append("sortField", params.sortField);
    if (params?.sortDirection) searchParams.append("sortDirection", params.sortDirection);
    if (params?.limit !== undefined) searchParams.append("limit", params.limit.toString());
    if (params?.offset !== undefined) searchParams.append("offset", params.offset.toString());

    const query = searchParams.toString() ? `?${searchParams.toString()}` : "";
    return api.get<ListSurveysResponse>(`/v1/survey/surveys${query}`);
  },

  getById: async (id: string): Promise<SingleSurveyResponse> => {
    return api.get<SingleSurveyResponse>(`/v1/survey/surveys/${id}`);
  },

  create: async (data: CreateSurveyBody): Promise<SingleSurveyResponse> => {
    return api.post<SingleSurveyResponse>("/v1/survey/surveys", data);
  },

  update: async (id: string, data: UpdateSurveyBody): Promise<SingleSurveyResponse> => {
    return api.put<SingleSurveyResponse>(`/v1/survey/surveys/${id}`, data);
  },

  remove: async (id: string): Promise<DeleteSurveyResponse> => {
    return api.del<DeleteSurveyResponse>(`/v1/survey/surveys/${id}`);
  },

  listResponses: async (
    params?: ListSurveyResponsesParams,
  ): Promise<{ responses: SurveyResponse[]; total: number; requestId: string }> => {
    const searchParams = new URLSearchParams();
    if (params?.surveyId) searchParams.append("surveyId", params.surveyId);
    if (params?.edition) searchParams.append("edition", params.edition);
    if (params?.publicationId !== undefined)
      searchParams.append("publicationId", params.publicationId.toString());
    if (params?.editionId) searchParams.append("editionId", params.editionId);
    if (params?.taxonomy) searchParams.append("taxonomy", params.taxonomy);
    if (params?.status) searchParams.append("status", params.status);
    if (params?.firmRef) searchParams.append("firmRef", params.firmRef);
    if (params?.refereeEmail) searchParams.append("refereeEmail", params.refereeEmail);
    if (params?.limit !== undefined) searchParams.append("limit", params.limit.toString());
    if (params?.offset !== undefined) searchParams.append("offset", params.offset.toString());

    const query = searchParams.toString() ? `?${searchParams.toString()}` : "";
    return api.get<{ responses: SurveyResponse[]; total: number; requestId: string }>(
      `/v1/survey/surveys/responses${query}`,
    );
  },

  getResponseById: async (
    responseId: string,
  ): Promise<{ response: SurveyResponse; requestId: string }> => {
    return api.get<{ response: SurveyResponse; requestId: string }>(
      `/v1/survey/surveys/responses/${responseId}`,
    );
  },

  publish: async (id: string): Promise<SingleSurveyResponse> => {
    return api.post<SingleSurveyResponse>(`/v1/survey/surveys/${id}/publish`);
  },

  getSupportedLanguages: async (): Promise<{ languages: string[]; requestId: string }> => {
    return api.get<{ languages: string[]; requestId: string }>("/v1/survey/surveys/languages");
  },

  translate: async (
    text: string,
    targetLanguage: string,
  ): Promise<{ translatedText: string; targetLanguage: string; requestId: string }> => {
    return api.post<{ translatedText: string; targetLanguage: string; requestId: string }>(
      "/v1/survey/surveys/translate",
      { text, targetLanguage },
    );
  },

  translateBatch: async (
    texts: string[],
    targetLanguage: string,
  ): Promise<{ translations: string[]; targetLanguage: string; requestId: string }> => {
    return api.post<{ translations: string[]; targetLanguage: string; requestId: string }>(
      "/v1/survey/surveys/translate-batch",
      { texts, targetLanguage },
    );
  },
};
